import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../../utils/api';

// ── Date helpers ──────────────────────────────────────────────────────────────
function todayISO() { return new Date().toISOString().slice(0, 10); }
function endOfWeekISO() {
  const d = new Date();
  d.setDate(d.getDate() + (7 - d.getDay()));
  return d.toISOString().slice(0, 10);
}
function fmtDate(d) {
  if (!d) return '—';
  const [y, m, dy] = d.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${dy} ${months[parseInt(m, 10) - 1]} ${y}`;
}
function isAgentOOF(agent) {
  if (!agent.oof_from || !agent.oof_to) return false;
  const t = todayISO();
  return t >= agent.oof_from && t <= agent.oof_to;
}

// ── Priority styles ───────────────────────────────────────────────────────────
const PRIORITY_STYLE = {
  critical: { bg: '#fee2e2', color: '#dc2626' },
  high:     { bg: '#ffedd5', color: '#c2410c' },
  medium:   { bg: '#fef3c7', color: '#b45309' },
  low:      { bg: '#f0fdf4', color: '#166534' },
};
const STATUS_STYLE = {
  open:        { bg: '#EDE9FE', color: '#8B5CF6' },
  in_progress: { bg: '#FEF3C7', color: '#D97706' },
};

function Badge({ label, bg, color }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 99,
      fontSize: 11, fontWeight: 700, background: bg, color,
      textTransform: 'capitalize', letterSpacing: 0.3,
    }}>
      {label}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function OOFManager() {
  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [eligibleCache, setEligibleCache] = useState({}); // { productName: [agents] }
  const [allAgents, setAllAgents] = useState([]); // fallback: all non-OOF agents
  const [periodMode, setPeriodMode] = useState('today'); // 'today' | 'week' | 'custom'
  const [customFrom, setCustomFrom] = useState(todayISO());
  const [customTo, setCustomTo] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  const [reassignMsg, setReassignMsg] = useState({}); // { ticketId: message }
  const searchRef = useRef(null);

  // Load all agents on mount
  useEffect(() => {
    apiFetch('/oof/agents').then(setAgents).catch(() => {});
  }, []);

  // Filter agents for typeahead
  const filtered = search.trim()
    ? agents.filter(a => a.name.toLowerCase().includes(search.toLowerCase()))
    : [];

  function selectAgent(agent) {
    setSelectedAgent(agent);
    setSearch(agent.name);
    setShowDropdown(false);
    setEligibleCache({});
    loadTickets(agent.id);
    loadAllAgents(agent.id);
  }

  function loadTickets(agentId) {
    apiFetch(`/oof/agents/${agentId}/tickets`).then(setTickets).catch(() => setTickets([]));
  }

  // Load all non-OOF agents for fallback reassignment
  function loadAllAgents(excludeId) {
    const today = todayISO();
    const available = agents.filter(a =>
      a.id !== excludeId &&
      (!a.oof_from || !a.oof_to || today < a.oof_from || today > a.oof_to)
    );
    setAllAgents(available);
  }

  function refreshAgent(agentId) {
    apiFetch('/oof/agents').then(all => {
      setAgents(all);
      const updated = all.find(a => a.id === agentId);
      if (updated) setSelectedAgent(updated);
    }).catch(() => {});
  }

  // Eligible agents per product (cached)
  const loadEligible = useCallback(async (productName) => {
    if (eligibleCache[productName] !== undefined) return;
    if (!productName || !selectedAgent) return;
    try {
      const data = await apiFetch(`/oof/eligible/${encodeURIComponent(productName)}?exclude=${selectedAgent.id}`);
      setEligibleCache(prev => ({ ...prev, [productName]: data }));
    } catch {
      setEligibleCache(prev => ({ ...prev, [productName]: [] }));
    }
  }, [eligibleCache, selectedAgent]);

  // Preload eligible agents for all ticket products when tickets load
  useEffect(() => {
    if (!selectedAgent || tickets.length === 0) return;
    const products = [...new Set(tickets.map(t => t.product_name).filter(Boolean))];
    products.forEach(p => loadEligible(p));
  }, [tickets, selectedAgent]); // eslint-disable-line

  async function handleReassign(ticketId, newAgentId, newAgentName) {
    try {
      await apiFetch(`/tickets/${ticketId}`, { method: 'PATCH', body: { assigned_to: newAgentId } });
      setReassignMsg(prev => ({ ...prev, [ticketId]: `✓ Reassigned to ${newAgentName}` }));
      setTimeout(() => {
        setReassignMsg(prev => { const n = { ...prev }; delete n[ticketId]; return n; });
        loadTickets(selectedAgent.id);
      }, 1500);
    } catch (err) {
      setReassignMsg(prev => ({ ...prev, [ticketId]: `✗ ${err.message || 'Error'}` }));
      setTimeout(() => {
        setReassignMsg(prev => { const n = { ...prev }; delete n[ticketId]; return n; });
      }, 3000);
    }
  }

  // Compute OOF period from mode
  function getOOFPeriod() {
    if (periodMode === 'today') return { from: todayISO(), to: todayISO() };
    if (periodMode === 'week')  return { from: todayISO(), to: endOfWeekISO() };
    return { from: customFrom, to: customTo };
  }

  async function handleMarkOOF() {
    if (!selectedAgent) return;
    const { from, to } = getOOFPeriod();
    setSaving(true);
    try {
      const updated = await apiFetch(`/oof/agents/${selectedAgent.id}`, {
        method: 'PATCH',
        body: { oof_from: from, oof_to: to },
      });
      setSelectedAgent(updated);
      refreshAgent(selectedAgent.id);
    } catch (err) {
      alert(err.message || 'Failed to set OOF');
    }
    setSaving(false);
  }

  async function handleClearOOF() {
    if (!selectedAgent) return;
    setSaving(true);
    try {
      const updated = await apiFetch(`/oof/agents/${selectedAgent.id}`, {
        method: 'PATCH',
        body: { oof_from: null, oof_to: null },
      });
      setSelectedAgent(updated);
      refreshAgent(selectedAgent.id);
    } catch (err) {
      alert(err.message || 'Failed to clear OOF');
    }
    setSaving(false);
  }

  const card = {
    background: 'var(--bg-surface)',
    borderRadius: 14,
    border: '1px solid var(--border-subtle)',
    boxShadow: 'var(--shadow-sm)',
  };

  const btnBase = {
    padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700,
    cursor: 'pointer', border: 'none', transition: 'all .15s',
  };

  return (
    <div className="page" style={{ padding: '28px 32px' }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div>
          <div className="page-title">Out of Office Manager</div>
          <div className="page-sub">Mark agents as out of office and reassign their tickets</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

        {/* ── Left Panel ────────────────────────────────────────────────── */}
        <div style={{ width: 400, flexShrink: 0, ...card, padding: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, color: 'var(--text-primary)' }}>
            Select Agent
          </div>

          {/* Search */}
          <div style={{ position: 'relative', marginBottom: 16 }} ref={searchRef}>
            <input
              className="input"
              placeholder="Search agent by name…"
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setShowDropdown(true);
                if (!e.target.value) { setSelectedAgent(null); setTickets([]); }
              }}
              onFocus={() => search && setShowDropdown(true)}
              style={{ fontSize: 13 }}
            />
            {showDropdown && filtered.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                borderRadius: 10, boxShadow: 'var(--shadow-md)', marginTop: 4, overflow: 'hidden',
              }}>
                {filtered.map(a => (
                  <div key={a.id}
                    onMouseDown={() => selectAgent(a)}
                    style={{
                      padding: '10px 14px', cursor: 'pointer', fontSize: 13,
                      borderBottom: '1px solid var(--border-subtle)',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--n-50)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{a.name}</span>
                    {isAgentOOF(a) && (
                      <span style={{ fontSize: 10, fontWeight: 700, background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: 99 }}>
                        OOF
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Agent details */}
          {selectedAgent && (
            <div>
              {/* Agent info card */}
              <div style={{
                background: 'var(--bg-app)', borderRadius: 10,
                padding: '14px 16px', marginBottom: 16,
                border: '1px solid var(--border-subtle)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>
                    {selectedAgent.name}
                  </div>
                  <Badge
                    label={selectedAgent.role}
                    bg="var(--n-100)"
                    color="var(--text-secondary)"
                  />
                </div>

                {/* Products */}
                {selectedAgent.agent_products && selectedAgent.agent_products.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                      Products
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {selectedAgent.agent_products.map((p, i) => (
                        <span key={i} style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 10px',
                          borderRadius: 99, background: '#dbeafe', color: '#1e40af',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}>
                          {p.name}
                          {p.tier && <span style={{ opacity: 0.7 }}>· {p.tier}</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* OOF status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {isAgentOOF(selectedAgent) ? (
                    <>
                      <span style={{
                        fontSize: 12, fontWeight: 700, background: '#fee2e2', color: '#dc2626',
                        padding: '3px 10px', borderRadius: 99,
                      }}>
                        ● OOF Active
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {fmtDate(selectedAgent.oof_from)} – {fmtDate(selectedAgent.oof_to)}
                      </span>
                    </>
                  ) : (
                    <span style={{
                      fontSize: 12, fontWeight: 700, background: '#d1fae5', color: '#065f46',
                      padding: '3px 10px', borderRadius: 99,
                    }}>
                      ● Available
                    </span>
                  )}
                </div>
              </div>

              {/* Period selector */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  OOF Period
                </div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  {[
                    { id: 'today', label: 'Today' },
                    { id: 'week',  label: 'This Week' },
                    { id: 'custom', label: 'Custom' },
                  ].map(opt => (
                    <button key={opt.id} onClick={() => setPeriodMode(opt.id)} style={{
                      ...btnBase,
                      padding: '5px 14px',
                      background: periodMode === opt.id ? '#0B6623' : 'var(--bg-app)',
                      color: periodMode === opt.id ? '#fff' : 'var(--text-secondary)',
                      border: `1.5px solid ${periodMode === opt.id ? '#0B6623' : 'var(--border-subtle)'}`,
                    }}>
                      {opt.label}
                    </button>
                  ))}
                </div>

                {periodMode === 'today' && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '6px 0' }}>
                    {fmtDate(todayISO())} (today only)
                  </div>
                )}
                {periodMode === 'week' && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '6px 0' }}>
                    {fmtDate(todayISO())} – {fmtDate(endOfWeekISO())}
                  </div>
                )}
                {periodMode === 'custom' && (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>From</div>
                      <input type="date" className="input" value={customFrom}
                        onChange={e => setCustomFrom(e.target.value)}
                        style={{ fontSize: 13 }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>To</div>
                      <input type="date" className="input" value={customTo}
                        onChange={e => setCustomTo(e.target.value)}
                        style={{ fontSize: 13 }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleMarkOOF} disabled={saving} className="btn btn-primary" style={{ flex: 1 }}>
                  {saving ? 'Saving…' : 'Mark Out of Office'}
                </button>
                {isAgentOOF(selectedAgent) && (
                  <button onClick={handleClearOOF} disabled={saving} className="btn btn-danger" style={{ flex: 1 }}>
                    Clear OOF
                  </button>
                )}
              </div>
            </div>
          )}

          {!selectedAgent && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-secondary)', fontSize: 13 }}>
              Search for an agent above to get started
            </div>
          )}
        </div>

        {/* ── Right Panel ───────────────────────────────────────────────── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...card, padding: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, color: 'var(--text-primary)' }}>
              {selectedAgent ? `Open Tickets — ${selectedAgent.name}` : 'Agent Tickets'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              {selectedAgent
                ? `${tickets.length} open / in-progress ticket${tickets.length !== 1 ? 's' : ''}`
                : 'Select an agent to view their tickets'}
            </div>

            {!selectedAgent && (
              <div style={{
                textAlign: 'center', padding: '48px 0',
                color: 'var(--text-secondary)', fontSize: 13,
                borderTop: '1px solid var(--border-subtle)',
              }}>
                No agent selected
              </div>
            )}

            {selectedAgent && tickets.length === 0 && (
              <div style={{
                textAlign: 'center', padding: '48px 0',
                color: 'var(--text-secondary)', fontSize: 13,
                borderTop: '1px solid var(--border-subtle)',
              }}>
                No open or in-progress tickets for this agent
              </div>
            )}

            {tickets.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--n-50)' }}>
                    {['Ticket #', 'Title', 'Customer', 'Product', 'Reassign To'].map(h => (
                      <th key={h} style={{
                        padding: '10px 12px', fontSize: 11, fontWeight: 700,
                        color: 'var(--text-secondary)', textAlign: 'left',
                        textTransform: 'uppercase', letterSpacing: 0.5,
                        borderBottom: '1px solid var(--border-subtle)',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tickets.map(t => {
                    const ps = PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.medium;
                    const ss = STATUS_STYLE[t.status] || STATUS_STYLE.open;
                    const eligible = eligibleCache[t.product_name] || [];
                    const msg = reassignMsg[t.id];
                    return (
                      <tr key={t.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '11px 12px', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                          {t.ticket_number}
                        </td>
                        <td style={{ padding: '11px 12px', maxWidth: 200 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>
                            {t.title}
                          </div>
                          <div style={{ display: 'flex', gap: 5 }}>
                            <Badge label={t.priority} bg={ps.bg} color={ps.color} />
                            <Badge label={t.status.replace('_', ' ')} bg={ss.bg} color={ss.color} />
                          </div>
                        </td>
                        <td style={{ padding: '11px 12px', fontSize: 13, color: 'var(--text-primary)', maxWidth: 130 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {t.customer_name || '—'}
                          </div>
                        </td>
                        <td style={{ padding: '11px 12px', fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                          {t.product_name || '—'}
                        </td>
                        <td style={{ padding: '11px 12px', minWidth: 180 }}>
                          {msg ? (
                            <span style={{
                              fontSize: 12, fontWeight: 600,
                              color: msg.startsWith('✓') ? '#065f46' : '#dc2626',
                            }}>
                              {msg}
                            </span>
                          ) : eligible.length > 0 ? (
                            <select
                              defaultValue=""
                              onChange={e => {
                                const val = e.target.value;
                                if (!val) return;
                                const agent = eligible.find(a => a.id === val);
                                handleReassign(t.id, val, agent?.name || val);
                                e.target.value = '';
                              }}
                              style={{
                                padding: '5px 8px', borderRadius: 7, fontSize: 12,
                                border: '1.5px solid var(--border-subtle)',
                                background: 'var(--bg-surface)', color: 'var(--text-primary)',
                                cursor: 'pointer', width: '100%',
                              }}
                            >
                              <option value="">— Reassign to… —</option>
                              {eligible.map(a => (
                                <option key={a.id} value={a.id}>{a.name}</option>
                              ))}
                            </select>
                          ) : (
                            <div>
                              <div style={{
                                fontSize: 11, color: '#92400e', background: '#fef3c7',
                                border: '1px solid #fcd34d', borderRadius: 6,
                                padding: '3px 8px', marginBottom: 5, fontWeight: 600,
                              }}>
                                ⚠ No eligible agents for {t.product_name || 'this product'}
                              </div>
                              {allAgents.length > 0 ? (
                                <select
                                  defaultValue=""
                                  onChange={e => {
                                    const val = e.target.value;
                                    if (!val) return;
                                    const agent = allAgents.find(a => a.id === val);
                                    handleReassign(t.id, val, agent?.name || val);
                                    e.target.value = '';
                                  }}
                                  style={{
                                    padding: '5px 8px', borderRadius: 7, fontSize: 12,
                                    border: '1.5px solid #fcd34d',
                                    background: '#fffbeb', color: '#78350f',
                                    cursor: 'pointer', width: '100%',
                                  }}
                                >
                                  <option value="">— Assign to any agent —</option>
                                  {allAgents.map(a => (
                                    <option key={a.id} value={a.id}>{a.name}</option>
                                  ))}
                                </select>
                              ) : (
                                <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
                                  No available agents
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
